import React, {
  ComponentProps,
  ComponentType,
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useRouterActions } from 'react-resource-router';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { WebItems } from '@atlassian/bitbucket-connect-react';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import RunPipelineModalInternalComponent from 'src/components/pipelines/components/run-pipeline';
import { ALLOWANCE_LIMIT } from 'src/components/pipelines/constants';
import {
  requestAllowance,
  requestRepositoryUserIsMember,
} from 'src/components/pipelines/redux/actions/pipelines';
import {
  getCapabilities,
  getRepository,
} from 'src/components/pipelines/redux/selectors/pipelines';
import { getPipelinesSettingsPageURL } from 'src/components/pipelines/urls';
import { createPipeline } from 'src/components/pipelines/utils/create-pipeline';
import { getPipelinePageURL } from 'src/components/pipelines/utils/links';
import { useAnalytics } from 'src/components/settings/analytics';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useParams } from 'src/router/hooks';
import { getTargetUserKey } from 'src/selectors/user-selectors';
import urls from 'src/urls';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  refName?: string;
  revision?: string;
};

// TODO: This component can be re-used in
// src/components/pipelines/pages/pipelines/onboarding/onboarding.tsx
// src/components/pipelines/pages/pipelines/pipelines-list-actions/pipelines-list-actions.tsx
// Note: need to add `fetchBranches` prop when using this component in the pages above
/* eslint @typescript-eslint/ban-types: "warn" */
export const RunPipelineModal: FC<Props> = ({
  isOpen,
  onClose,
  refName,
  revision,
}) => {
  const { push } = useRouterActions();
  const dispatch = useDispatch();

  const capabilities = useSelector(getCapabilities);
  const repository = useSelector(getRepository);
  const targetUserKey = useSelector(getTargetUserKey);

  const removeAllowanceSagaEnabled = useStatsigGate(
    StatsigFeatureKeys.removeAllowanceSagaEnabled
  ) as boolean;
  useEffect(() => {
    dispatch(requestRepositoryUserIsMember('contributor'));
    dispatch(requestRepositoryUserIsMember('admin'));
    if (!removeAllowanceSagaEnabled && targetUserKey) {
      dispatch(requestAllowance(targetUserKey));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { repositoryOwner, repositorySlug } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
  }>();
  const repoPath = `${repositoryOwner}/${repositorySlug}`;

  const fetchCreatePipeline = useCallback(
    data => createPipeline(repository, data),
    [repository]
  );

  const openPipelinePage = useCallback(
    (buildNumber: number) => {
      push(getPipelinePageURL({ buildNumber }, repoPath));
      onClose();
    },
    [onClose, push, repoPath]
  );

  const isOverAllowance =
    capabilities.limited &&
    capabilities.allowancePercentageUsed >= ALLOWANCE_LIMIT;

  const plansPageUrl = targetUserKey
    ? urls.ui.workspacePlansAndBilling(repository.getWorkplaceSlug())
    : undefined;

  const settingsPageUrl = `${getPipelinesSettingsPageURL(repoPath)}${
    location.search
  }`;
  return isOpen ? (
    <RunPipelineModalInternalComponent
      fetchCreatePipeline={fetchCreatePipeline}
      isOverAllowance={isOverAllowance}
      isPipelinesDisabled={!capabilities.pipelinesEnabled}
      isRepoReadOnly={!repository.canWrite}
      onCloseDialog={onClose}
      openPipelinePage={openPipelinePage}
      plansPageUrl={plansPageUrl}
      refName={refName}
      revision={revision}
      settingsPageUrl={settingsPageUrl}
      showBlanket
    />
  ) : null;
};

type OpenRunPipelineModal = (options: {
  refName?: string;
  revision?: string;
}) => void;

export const RunPipelineContext = createContext<{
  close: () => void;
  open: OpenRunPipelineModal;
}>({
  close: () => undefined,
  open: () => undefined,
});

/**
 * HOC: wrap a component with "run pipeline" context provider and modal.
 */
export function withRunPipelineModal<P>(
  Comp: ComponentType<P>,
  openModalAnalyticsEvent?: string
): ComponentType<P> {
  return props => {
    const { publishButtonClickEvent } = useAnalytics({
      source: 'run-pipeline-modal',
    });

    const [isOpen, setIsOpen] = useState(false);
    const [refName, setRefName] = useState<string>();
    const [revision, setRevision] = useState<string>();

    const open = useCallback<OpenRunPipelineModal>(
      ({ refName: ref, revision: rev }) => {
        setIsOpen(true);
        setRefName(ref);
        setRevision(rev);

        if (openModalAnalyticsEvent) {
          publishButtonClickEvent(openModalAnalyticsEvent);
        }
      },
      [publishButtonClickEvent]
    );

    const close = useCallback(() => {
      setIsOpen(false);
      setRefName(undefined);
      setRevision(undefined);
    }, []);
    const contextValue = useMemo(
      () => ({
        close,
        open,
      }),
      [close, open]
    );

    return (
      <RunPipelineContext.Provider value={contextValue}>
        <Comp {...props} />
        <ModalTransition>
          <RunPipelineModal
            isOpen={isOpen}
            onClose={close}
            refName={refName}
            revision={revision}
          />
        </ModalTransition>
      </RunPipelineContext.Provider>
    );
  };
}

// A temporary workaround to hide specific webItems defined in the Pipelines Connect descriptor.
// So we can control Connect webItems with feature flags (which is hard to do via descriptors).
// To be cleaned up in CICD-1337, and restore it back to
// import { WebItems } from '@atlassian/bitbucket-connect-react';
type WebItemsProps = ComponentProps<typeof WebItems>;
/* eslint @typescript-eslint/ban-types: "warn" */
export const WebItemsWithoutRunPipeline: FC<WebItemsProps> = props => {
  const { children, ...otherProps } = props;
  return (
    <WebItems {...otherProps}>
      {items =>
        items.filter(item => item?.props?.module?.app_key !== 'pipelines')
      }
    </WebItems>
  );
};
